<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getEcommerceHowWeDev } from "../data/getEcommerceHowWeDev.js";
	import { getFaqsEcommerce } from "../data/getFaqsEcommerce.js";
	import { getReasonsEcommerce } from "../data/getReasonsEcommerce.js";
	import { getServicesEcommerce } from "../data/getServicesEcommerce.js";
	import type { EcommerceProps } from "./EcommerceProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: EcommerceProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="S vlastním a rychlým e-commerce řešením cg[[vytřete konkurenci zrak]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Proč se omezovat krabicovým e-shopem, když od nás můžete mít výkonné e-commerce řešení na míru sp[[a být o krok před konkurencí?]] "
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Zeptat se na podrobnosti" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8"
		gridDesc="container-6"
		centered
	>
		{#snippet headline()}
			<Formatted text="Tvorba e-shopu od cg[[vývoje]] až po cg[[napojení systémů]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Co kdybyste měli e-shop, který vás nebude omezovat v růstu podnikání a s funkcionalitami přesně podle svých představ? sp[[Tým Fastest ho pro vás rád vytvoří.]] Dlouhodobě firmám dodáváme komplexní e-commerce řešení na zakázku."
				/>
			</p>
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getServicesEcommerce(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Nezávazně poptat" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section gridHeadline="container-8" gridDesc="container-6" centered>
		{#snippet headline()}
			<Formatted text="E-commerce řešení děláme pro online obchody cg[[všech velikostí]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="sp[[Nezáleží, jestli rozjíždíte svůj první e-shop, nebo se na trhu pohybujete řadu let.]] Máme zkušenosti s vývojem komplexních řešení na míru pro malé online obchody i pro velké e-commerce platformy odbavující tisíce objednávek denně. "
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[5 důvodů]], proč si od nás nechat vyvinout e-shop" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getReasonsEcommerce(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Kdo patří mezi naše spokojené cg[[e-commerce zákazníky]]?" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.Ecommerce} />
		</div>
		<div class="flex flex-col mt-8 lg:mt-12 gap-3">
			<Link
				variant={ButtonVariant.GreenOutlineLightBg}
				class="mx-auto flex items-center justify-center"
				href={getReferencePath(tenant)}
			>
				Zobrazit více
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="Jak probíhá cg[[spolupráce]] s e-commerce klienty" />
			<span class="block text-2md font-bold mt-6"
				><Formatted
					text="Provedeme vás cg[[celým procesem]] od úvodní konzultace přes vývoj až po údržbu a podporu."
				/></span
			>
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getEcommerceHowWeDev(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[e-commerce řešením]]" />
		{/snippet}
		<FaqItems faqs={getFaqsEcommerce(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
